import {
  TLWfOwnershipScope,
  TLWfWorkflowStatus,
  type TLWorkflowListItemResponse,
} from '@/api/gen/tessera-backend-api.schemas';

export type WorkflowOwnershipFilterValue = 'all' | 'owned' | 'shared';

export const OWNERSHIP_SCOPE: Record<WorkflowOwnershipFilterValue, TLWfOwnershipScope> = {
  all: TLWfOwnershipScope.ALL,
  owned: TLWfOwnershipScope.OWNED,
  shared: TLWfOwnershipScope.SHARED,
};

export const lastExecutionTimestamp = (workflow: TLWorkflowListItemResponse): number =>
  Date.parse(workflow.execution_stats?.last_execution_at ?? '');

/**
 * Splits a table selection into the ids each bulk archive action can actually act on.
 *
 * The two actions are mutually exclusive per workflow — only ACTIVE can be archived and only
 * ARCHIVED can be unarchived — so a mixed selection drives two separate buttons rather than one
 * that would report half its ids as failures. Ids not present in `workflows` are dropped: the
 * selection survives across pages, and a bulk call must not name a row this page can't vouch for.
 *
 * @param workflows - The rows currently loaded in the table.
 * @param selectedIds - Workflow ids the user has selected.
 * @returns `archivable` (ACTIVE) and `unarchivable` (ARCHIVED) id lists, in row order.
 *
 * @example
 * partitionSelectionByArchiveAction(rows, new Set(['a', 'b']))
 * // => { archivable: ['a'], unarchivable: ['b'] }  // when a is ACTIVE and b is ARCHIVED
 */
export const partitionSelectionByArchiveAction = (
  workflows: TLWorkflowListItemResponse[],
  selectedIds: Set<string>,
): { archivable: string[]; unarchivable: string[] } => {
  const archivable: string[] = [];
  const unarchivable: string[] = [];
  for (const workflow of workflows) {
    if (!selectedIds.has(workflow.workflow_id)) continue;
    if (workflow.status === TLWfWorkflowStatus.ACTIVE) archivable.push(workflow.workflow_id);
    else if (workflow.status === TLWfWorkflowStatus.ARCHIVED) unarchivable.push(workflow.workflow_id);
  }
  return { archivable, unarchivable };
};

/** Tabs on the loom list page. Values double as the visible tab labels. */
export enum LoomTab {
  WORKFLOWS = 'Workflows',
  EXECUTIONS = 'Executions',
  TEMPLATES = 'Templates',
}

/**
 * Narrows an untrusted tab string (URL param, localStorage) to a {@link LoomTab}.
 *
 * @param value - Candidate tab label, or `null` when the source held nothing.
 * @returns The matching tab, or `undefined` when the value is absent or unrecognised.
 */
export const parseLoomTab = (value: string | null): LoomTab | undefined =>
  value && (Object.values(LoomTab) as string[]).includes(value) ? (value as LoomTab) : undefined;

/** Sub-heading for each landing-page tab. Titles come from the tab label itself, which matches the sidenav. */
export const LOOM_TAB_DESCRIPTION: Record<LoomTab, string> = {
  [LoomTab.WORKFLOWS]: 'Manage your workflows',
  [LoomTab.EXECUTIONS]: 'Review recent workflow runs',
  [LoomTab.TEMPLATES]: 'Start a workflow from a template',
};
